<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <label class="input-label form-label">
         {{ label }} <span v-if="required" style="color: red">*</span>
      </label>
      <div class="d-flex justify-content-end mb-1">
        <b-button
          v-if="showSourceModeButton"
          variant="primary"
          size="xs"
          @click="toggleSourceMode"
        >
          {{ isSourceMode ? $t("showRichTextEditor") : $t("showRawHtml") }}
        </b-button>
      </div>
    </div>

    <div v-if="isSourceMode">
      <textarea
        v-model="rawHtml"
        class="form-control raw-html-mode"
        rows="15"
        @input="emitRawHtml"
      ></textarea>
    </div>

    <vue-editor
      v-else
      :value="editorContent"
      @input="onEditorInput"
      :editorOptions="editorOptions"
      ref="editor"
    />
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import "quill/dist/quill.snow.css";

export default {
  name: "QuillEditor",
  components: { VueEditor },
  props: {
    value: String,
    label: String,
    required: Boolean,
    showSourceModeButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isSourceMode: false,
      rawHtml: "",
      editorContent: "",
      lastRawHtmlValue: "",
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["blockquote", "code-block"],
            ["link", "image"],
            ["clean"],
          ],
        },
      },
    };
  },
  watch: {
    value(newVal) {
      if (this.isSourceMode) {
        if (newVal !== this.rawHtml) {
          this.rawHtml = newVal;
          this.lastRawHtmlValue = newVal;
        }
      } else if (newVal !== this.editorContent) {
        this.editorContent = newVal;
        this.lastRawHtmlValue = "";
        this.$nextTick(() => {
          this.updateQuillContent(newVal);
        });
      }
    },
  },
  mounted() {
    this.editorContent = this.value;
    this.rawHtml = this.value;
    this.lastRawHtmlValue = this.value;

    this.$nextTick(() => {
      if (this.$refs.editor?.quill) {
        this.updateQuillContent(this.value);
      }
    });
  },
  methods: {
    decodeHtmlEntities(text) {
      if (!text) return text;
      const entityMap = {
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&#39;": "'",
        "&#x27;": "'",
        "&#x2F;": "/",
      };
      return text
        .replace(/&(amp|lt|gt|quot|#39|#x27|#x2F);/gi, (match) => {
          return entityMap[match] || match;
        })
        .replace(/&#(\d+);/g, (match, dec) => {
          return String.fromCharCode(dec);
        })
        .replace(/&#x([a-f\d]+);/gi, (match, hex) => {
          return String.fromCharCode(parseInt(hex, 16));
        });
    },
    updateQuillContent(html) {
      const quill = this.$refs.editor?.quill;
      if (quill && html !== undefined && html !== null) {
        quill.root.innerHTML = "";
        quill.clipboard.dangerouslyPasteHTML(0, html);
      }
    },
    toggleSourceMode() {
      const quill = this.$refs.editor?.quill;

      if (!this.isSourceMode) {
        if (this.lastRawHtmlValue) {
          this.rawHtml = this.lastRawHtmlValue;
        } else if (this.value !== undefined && this.value !== null) {
          this.rawHtml = this.value;
        } else {
          const quillHtml = quill?.root.innerHTML || "";
          this.rawHtml = this.decodeHtmlEntities(quillHtml);
        }
        this.isSourceMode = true;
      } else {
        this.lastRawHtmlValue = this.rawHtml;
        this.isSourceMode = false;
        this.$nextTick(() => {
          this.updateQuillContent(this.rawHtml);
          this.editorContent = this.rawHtml;
          this.$emit("input", this.rawHtml);
        });
      }
    },
    emitRawHtml() {
      this.lastRawHtmlValue = this.rawHtml;
      this.$emit("input", this.rawHtml);
    },
    onEditorInput(val) {
      if (!this.isSourceMode) {
        this.editorContent = val;
        this.lastRawHtmlValue = "";
        const decodedVal = this.decodeHtmlEntities(val);
        this.$emit("input", decodedVal);
      }
    },
  },
};
</script>

<style scoped>
.raw-html-mode {
  white-space: pre-wrap;
  font-family: monospace;
  background-color: #f8f9fa;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  resize: vertical;
}
</style>
<style lang="scss">
body.dark-layout {
  .raw-html-mode {
    background-color: #091420;
    border-color: rgba(255, 255, 255, 0.2);
    color: #fff;
  }
}
</style>
